/**
 * Phase 15.6J — Reusable Play button that drives the global player.
 *
 * Drop this into any track card to give it instant playback without
 * navigating away. Optional `queue` + `sourceLabel` props enable
 * next/previous through a list and a "Playing from X" context label.
 */
import { usePlayer } from "./PlayerProvider";

const PlayIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 20 20" fill="currentColor">
    <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
  </svg>
);
const PauseIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 20 20" fill="currentColor">
    <rect x="5" y="3.5" width="3.5" height="13" rx="1" />
    <rect x="11.5" y="3.5" width="3.5" height="13" rx="1" />
  </svg>
);

export const PlayButton = ({
  track,
  queue,
  sourceLabel,
  size = 36,
  className = "",
  variant = "filled", // "filled" | "ghost"
  testIdSuffix = "",
}) => {
  const { play, toggle, track: current, isPlaying } = usePlayer();
  const trackId = track?.trackId || track?.submission_id || track?.id;
  const isCurrent = current && trackId && current.trackId === trackId;
  const showPause = isCurrent && isPlaying;

  const handleClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (isCurrent) { toggle(); return; }
    play(track, { queue, sourceLabel });
  };

  const base =
    "rounded-full flex items-center justify-center shadow-lg transition-all transform hover:scale-105 flex-shrink-0";
  const filled =
    "bg-gradient-to-br from-purple-500 to-pink-500 hover:from-purple-400 hover:to-pink-400 text-white shadow-purple-500/30";
  const ghost =
    "bg-black/60 hover:bg-black/80 text-white border border-purple-500/30";

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-label={showPause ? "Pause" : "Play"}
      data-testid={`play-btn${testIdSuffix ? `-${testIdSuffix}` : ""}${trackId ? `-${trackId}` : ""}`}
      data-state={showPause ? "playing" : "paused"}
      className={`${base} ${variant === "ghost" ? ghost : filled} ${className}`}
      style={{ width: size, height: size }}
    >
      {showPause ? <PauseIcon size={Math.round(size * 0.45)} /> : <PlayIcon size={Math.round(size * 0.45)} />}
    </button>
  );
};

export default PlayButton;
